import type {
  TeamAiSkill,
  TeamAiSkillAssignment,
  TeamAiSkillAssignmentInput,
  TeamAiSkillCreateInput,
  TeamAiSkillEvaluationRun,
  TeamAiSkillVersion,
} from './admin-team-ai-skills.types';

const SKILLS_ENDPOINT = '/api/admin/team-ai/skills';

interface ErrorResponse {
  error?: string;
}

async function requestJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(input, {
    credentials: 'include',
    ...init,
    headers: init?.body
      ? { 'Content-Type': 'application/json', ...init.headers }
      : init?.headers,
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(payload.error || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export async function listTeamAiSkills(): Promise<TeamAiSkill[]> {
  const response = await requestJson<{ skills: TeamAiSkill[] }>(
    SKILLS_ENDPOINT,
  );
  return response.skills;
}

export async function createTeamAiSkill(
  input: TeamAiSkillCreateInput,
  imported = false,
): Promise<void> {
  await requestJson<{ skill: TeamAiSkill; version: TeamAiSkillVersion }>(
    imported ? `${SKILLS_ENDPOINT}/import` : SKILLS_ENDPOINT,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export async function createTeamAiSkillVersion(
  skillId: string,
  content: string,
): Promise<TeamAiSkillVersion> {
  const response = await requestJson<{ version: TeamAiSkillVersion }>(
    `${SKILLS_ENDPOINT}/${skillId}/versions`,
    { method: 'POST', body: JSON.stringify({ content }) },
  );
  return response.version;
}

export async function updateTeamAiSkillDraft(
  versionId: string,
  content: string,
): Promise<TeamAiSkillVersion> {
  const response = await requestJson<{ version: TeamAiSkillVersion }>(
    `${SKILLS_ENDPOINT}/versions/${versionId}`,
    { method: 'PUT', body: JSON.stringify({ content }) },
  );
  return response.version;
}

export async function evaluateTeamAiSkillVersion(
  versionId: string,
  maxOutputTokens: number,
): Promise<TeamAiSkillEvaluationRun> {
  const response = await requestJson<{
    evaluationRun: TeamAiSkillEvaluationRun;
  }>(`${SKILLS_ENDPOINT}/versions/${versionId}/evaluate`, {
    method: 'POST',
    body: JSON.stringify({ maxOutputTokens }),
  });
  return response.evaluationRun;
}

export async function listTeamAiSkillEvaluations(
  versionId: string,
): Promise<TeamAiSkillEvaluationRun[]> {
  const response = await requestJson<{
    evaluationRuns: TeamAiSkillEvaluationRun[];
  }>(`${SKILLS_ENDPOINT}/versions/${versionId}/evaluations`);
  return response.evaluationRuns;
}

export async function changeTeamAiSkillVersionState(
  versionId: string,
  action: 'publish' | 'retire',
): Promise<TeamAiSkillVersion> {
  const response = await requestJson<{ version: TeamAiSkillVersion }>(
    `${SKILLS_ENDPOINT}/versions/${versionId}/${action}`,
    { method: 'POST' },
  );
  return response.version;
}

export async function assignTeamAiSkill(
  input: TeamAiSkillAssignmentInput,
): Promise<TeamAiSkillAssignment> {
  const response = await requestJson<{ assignment: TeamAiSkillAssignment }>(
    `${SKILLS_ENDPOINT}/assignments`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return response.assignment;
}

export async function removeTeamAiSkillAssignment(
  assignmentId: string,
): Promise<void> {
  const response = await fetch(
    `${SKILLS_ENDPOINT}/assignments/${assignmentId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(payload.error || `Request failed (${response.status})`);
  }
}
